/**
 * Metro 2 Legal Basis Summary Component
 * 
 * Shows legal authorities applicable to the Metro 2 dispute:
 * - FCRA sections
 * - Regulation V
 * - CDIA Metro 2 Format Guidelines
 * - State laws if applicable
 */

import React from 'react';
import {
  Scale,
  BookOpen,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Info,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

// Legal authority categories
const AUTHORITY_CATEGORIES = {
  fcra: {
    label: 'FCRA',
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-700',
    description: 'Fair Credit Reporting Act',
  },
  regulation_v: {
    label: 'Regulation V',
    color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200 dark:border-purple-700',
    description: 'Federal Reserve Regulation V',
  },
  metro2: {
    label: 'Metro 2',
    color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700',
    description: 'CDIA Metro 2 Format Guidelines',
  },
  state: {
    label: 'State Law',
    color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-700',
    description: 'State Consumer Protection Laws',
  },
};

// Legal Authority Item
const LegalAuthorityItem = ({ authority, expanded, onToggle }) => {
  const category = AUTHORITY_CATEGORIES[authority.category] || AUTHORITY_CATEGORIES.fcra;

  return (
    <div className="border border-stone-200 dark:border-stone-700 rounded-lg overflow-hidden">
      <button
        className="w-full p-3 flex items-center justify-between text-left bg-white dark:bg-stone-900 hover:bg-stone-50 dark:hover:bg-stone-800"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <Badge variant="outline" className={category.color}>
            {category.label}
          </Badge>
          <span className="font-medium text-stone-800 dark:text-stone-200">
            {authority.citation || authority.name}
          </span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {expanded && (
        <div className="p-3 bg-stone-50 dark:bg-stone-800/50 border-t border-stone-200 dark:border-stone-700">
          {/* Authority Title */}
          {authority.title && (
            <h4 className="font-medium text-stone-800 dark:text-stone-200 mb-2">
              {authority.title}
            </h4>
          )}

          {/* Description */}
          {authority.description && (
            <p className="text-sm text-stone-600 dark:text-stone-400 mb-3">
              {authority.description}
            </p>
          )}

          {/* Key Points */}
          {authority.key_points && authority.key_points.length > 0 && (
            <div className="mb-3">
              <h5 className="text-xs font-medium text-stone-500 dark:text-stone-400 uppercase mb-2">Key Points</h5>
              <ul className="space-y-1">
                {authority.key_points.map((point, idx) => (
                  <li key={idx} className="text-sm text-stone-600 dark:text-stone-400 flex items-start gap-2">
                    <span className="text-emerald-500 mt-1">•</span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Relevance to Dispute */}
          {authority.relevance && (
            <div className="p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded text-sm text-emerald-700 dark:text-emerald-300">
              <strong>Relevance:</strong> {authority.relevance}
            </div>
          )}

          {/* External Link */}
          {authority.url && (
            <a 
              href={authority.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline mt-2"
            >
              View Full Text <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      )}
    </div>
  );
};

// Main Metro 2 Legal Basis Summary Component
const Metro2LegalBasisSummary = ({
  authorities = [],
  issueCategories = [],
  compact = false,
}) => {
  const [expandedItems, setExpandedItems] = React.useState({});
  const [showAll, setShowAll] = React.useState(false);

  // Default authorities if none provided
  const defaultAuthorities = [
    {
      category: 'fcra',
      citation: '15 U.S.C. § 1681e(b)',
      title: 'Accuracy of Consumer Reports',
      description: 'Consumer reporting agencies must follow reasonable procedures to assure maximum possible accuracy of information.',
      key_points: [
        'CRAs must maintain reasonable procedures',
        'Information must be accurate at time of reporting',
        'Applies to all consumer report data',
      ],
      relevance: 'Furnisher reporting inaccurate Metro 2 data violates accuracy requirements.',
    },
    {
      category: 'fcra',
      citation: '15 U.S.C. § 1681i',
      title: 'Investigation of Disputed Information',
      description: 'CRAs must reinvestigate disputed information within 30 days and delete/modify unverifiable information.',
      key_points: [
        '30-day investigation requirement',
        'Must contact furnisher for verification',
        'Delete unverifiable information',
        'Provide results in writing',
      ],
      relevance: 'Bureau must investigate Metro 2 field conflicts and verify accuracy.',
    },
    {
      category: 'fcra',
      citation: '15 U.S.C. § 1681s-2(b)',
      title: 'Furnisher Duties Upon Notice of Dispute',
      description: 'Furnishers must conduct reasonable investigation when notified of dispute by CRA.',
      key_points: [
        'Must investigate disputed information',
        'Review all relevant information',
        'Report results to CRA',
        'Correct inaccurate information',
      ],
      relevance: 'Furnisher must verify Metro 2 field values when disputed.',
    },
    {
      category: 'regulation_v',
      citation: '12 CFR § 1022.42',
      title: 'Reasonable Policies and Procedures',
      description: 'Furnishers must establish reasonable written policies and procedures regarding accuracy and integrity.',
      key_points: [
        'Written accuracy policies required',
        'Integrity of furnished information',
        'Procedures to update information',
      ],
      relevance: 'Metro 2 field conflicts may indicate policy failures.',
    },
    {
      category: 'metro2',
      citation: 'CDIA Metro 2 Format',
      title: 'Metro 2 Credit Reporting Resource Guide',
      description: 'Industry standard for furnisher credit data reporting format and field definitions.',
      key_points: [
        'Standardized field codes (BS-1 through BS-29)',
        'Account status and payment status codes',
        'Payment history profile requirements',
        'Date reporting requirements',
      ],
      relevance: 'Metro 2 field inconsistencies indicate non-compliance with industry standards.',
    },
  ];

  const displayAuthorities = authorities.length > 0 ? authorities : defaultAuthorities;
  const visibleAuthorities = showAll ? displayAuthorities : displayAuthorities.slice(0, compact ? 2 : 3);

  const toggleItem = (index) => {
    setExpandedItems(prev => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Group authorities by category
  const categoryCounts = displayAuthorities.reduce((acc, auth) => {
    acc[auth.category] = (acc[auth.category] || 0) + 1;
    return acc;
  }, {});

  return (
    <Card className="border-stone-200 dark:border-stone-700" data-testid="metro2-legal-basis-summary">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2 text-stone-800 dark:text-stone-200">
            <Scale className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            Legal Basis Summary
          </CardTitle>
          <div className="flex gap-2">
            {Object.entries(categoryCounts).map(([cat, count]) => {
              const config = AUTHORITY_CATEGORIES[cat];
              return config ? (
                <Badge key={cat} variant="outline" className={`text-xs ${config.color}`}>
                  {config.label}: {count}
                </Badge>
              ) : null;
            })}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Introduction */}
        {!compact && (
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-blue-700 dark:text-blue-300">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>
                Metro 2 is the industry furnishing format used to structure reported account data. 
                While Metro 2 is not itself a statute, Metro 2 field inconsistencies may support a 
                dispute under the FCRA and Regulation V because the reporting may be inaccurate, 
                incomplete, unverifiable, or misleading.
              </p>
            </div>
          </div>
        )}

        {/* Authority List */}
        <div className="space-y-2">
          {visibleAuthorities.map((authority, index) => (
            <LegalAuthorityItem
              key={index}
              authority={authority}
              expanded={expandedItems[index]}
              onToggle={() => toggleItem(index)}
            />
          ))}
        </div>

        {/* Show More/Less */}
        {displayAuthorities.length > (compact ? 2 : 3) && (
          <Button
            variant="ghost"
            className="w-full text-stone-600 dark:text-stone-400"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? (
              <>Show Less <ChevronUp className="w-4 h-4 ml-1" /></>
            ) : (
              <>Show {displayAuthorities.length - visibleAuthorities.length} More <ChevronDown className="w-4 h-4 ml-1" /></>
            )}
          </Button>
        )}

        {/* Issue-Specific Authorities */}
        {issueCategories.length > 0 && !compact && (
          <div className="pt-3 border-t border-stone-200 dark:border-stone-700">
            <h4 className="text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
              Applicable to Your Issues
            </h4>
            <div className="flex flex-wrap gap-2">
              {issueCategories.map((issue, idx) => (
                <Badge key={idx} variant="outline" className="text-xs bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400">
                  {typeof issue === 'string' ? issue.replace(/_/g, ' ') : issue.label}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default Metro2LegalBasisSummary;
export { LegalAuthorityItem, AUTHORITY_CATEGORIES };
